var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { actions } from "store/actions";
import { selectors } from "store/selectors";
import { LAYERS, DEFAULT_LAYER } from "store/layers";
import { TactonApplication } from "api/tacton/types";
import { MenuItem } from "store/types";
import { hasAutoOpenedChartStorageKey, hasToggledChartStorageKey, } from "ui/components/panels/graphs/constants";
/*
 * The calculation pipeline: what triggers a computation request, when the
 * with-energy-saving follow-up runs, and when a finished result is revealed.
 *
 * This used to be a React observer component whose effects re-derived "when to
 * act" from dependency arrays — and both of its production bugs were exactly
 * that: a selector missing from a deps list silently froze part of the
 * pipeline (box edits never recalculated; the reveal never retried when the
 * non-displayed layer settled last). Listeners compare the previous and
 * current state directly on every dispatch, so there is no separate list to
 * keep in sync.
 *
 * Calculations only run while `calculationsEnabled` is set (the system-design
 * page is shown — see StoreProvider); enabling is itself a trigger, replacing
 * the old component's fire-on-mount.
 */
var CALC_WITHOUT_ES_DEBOUNCE_TIME = 2000;
var CALC_WITH_ES_DEBOUNCE_TIME = 2000;
var hasSystemData = function (state) {
    var systemData = selectors.selectSystemDataDefineParameters(state);
    return !!systemData && Object.keys(systemData.parameters).length > 0;
};
/*
 * Everything the computation request is built from. A change in any of these
 * (by reference — the reducers behind them are idempotent, and the derived
 * cup-to-box selectors are value-memoized) queues a fresh calculation.
 */
var calculationInputs = function (state) { return [
    selectors.selectNodes(state),
    selectors.selectEdges(state),
    selectors.selectAttachments(state),
    selectors.selectCardboardBoxes(state),
    selectors.selectCupsOutsideOfObjectByLayer(state),
    selectors.selectCupBoxAssignmentsByLayer(state),
    selectors.selectSystemDataDefineParameters(state),
    selectors.selectManualCalculationInput(state),
    selectors.selectActiveApplication(state),
]; };
var inputsChanged = function (current, previous) {
    var currentInputs = calculationInputs(current);
    var previousInputs = calculationInputs(previous);
    return currentInputs.some(function (input, i) { return input !== previousInputs[i]; });
};
var layersToCalculate = function (state) {
    // Only the cardboard application has stacking layers — and only the layers
    // actually in use are computed: a single-layer system pays for exactly one
    // calculation, whether it is cardboard or not.
    return selectors.selectActiveApplication(state) === TactonApplication.CARDBOARD
        ? selectors.selectLayersInUse(state)
        : [DEFAULT_LAYER];
};
export var generateListeners = function (startAppListening) {
    /*
     * Queue a (debounced) calculation of every in-play layer whenever the system
     * changes, and when calculations become enabled. The pending flags are set
     * immediately so a simulation started during the debounce window shows
     * progress instead of briefly revealing stale results; a newer change
     * cancels the debounce, and cancellation also aborts requests already in
     * flight.
     */
    var triggerCalculationOnSystemChange = function () {
        return startAppListening({
            predicate: function (_action, currentState, previousState) {
                var enabled = selectors.selectCalculationsEnabled(currentState);
                var wasEnabled = selectors.selectCalculationsEnabled(previousState);
                if (!enabled) {
                    // Cancel queued/in-flight work the moment calculations are disabled.
                    return wasEnabled;
                }
                if (!hasSystemData(currentState))
                    return false;
                return !wasEnabled || inputsChanged(currentState, previousState);
            },
            effect: function (_action, listenerApi) { return __awaiter(void 0, void 0, void 0, function () {
                var state, layers, promises, results;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            listenerApi.cancelActiveListeners();
                            state = listenerApi.getState();
                            if (!selectors.selectCalculationsEnabled(state))
                                return [2 /*return*/];
                            layers = layersToCalculate(state);
                            listenerApi.dispatch(actions.markCalculationPending(layers));
                            listenerApi.dispatch(actions.setHasPendingSanityCheck(true));
                            return [4 /*yield*/, listenerApi.delay(CALC_WITHOUT_ES_DEBOUNCE_TIME)];
                        case 1:
                            _a.sent();
                            promises = layers.map(function (layer) {
                                return listenerApi.dispatch(actions.getSystemPerformance(layer));
                            });
                            listenerApi.signal.addEventListener("abort", function () {
                                return promises.forEach(function (promise) { return promise.abort(); });
                            });
                            return [4 /*yield*/, Promise.all(promises)];
                        case 2:
                            results = _a.sent();
                            if (results.some(function (result) {
                                return result.meta.requestStatus === "rejected" && result.meta.aborted;
                            })) {
                                return [2 /*return*/];
                            }
                            listenerApi.dispatch(actions.setHasPendingSanityCheck(false));
                            return [2 /*return*/];
                    }
                });
            }); },
        });
    };
    /*
     * Once a layer's without-ES request has completed, follow up with the
     * (debounced) with-ES request for that layer. A layer going back to pending
     * cancels the follow-up.
     *
     * Keyed on the without-ES result alone, never on `preloadedData` as a whole:
     * the with-ES result is written into that same object, so watching it made
     * every ES result queue the next ES request — an endless loop.
     */
    var esReadiness = function (state, layer) {
        var _a;
        var layerPerformance = state.systemPerformance.byLayer[layer];
        var energySavingOff = (_a = layerPerformance.preloadedData) === null || _a === void 0 ? void 0 : _a.energySavingOff;
        return {
            ready: !layerPerformance.isPerformanceDataPending && !!energySavingOff,
            energySavingOff: energySavingOff,
        };
    };
    var triggerEsCalculation = function (layer) { return function () {
        return startAppListening({
            predicate: function (_action, currentState, previousState) {
                var current = esReadiness(currentState, layer);
                var previous = esReadiness(previousState, layer);
                return (current.ready !== previous.ready ||
                    (current.ready &&
                        current.energySavingOff !== previous.energySavingOff));
            },
            effect: function (_action, listenerApi) { return __awaiter(void 0, void 0, void 0, function () {
                var promise;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            listenerApi.cancelActiveListeners();
                            if (!esReadiness(listenerApi.getState(), layer).ready)
                                return [2 /*return*/];
                            return [4 /*yield*/, listenerApi.delay(CALC_WITH_ES_DEBOUNCE_TIME)];
                        case 1:
                            _a.sent();
                            promise = listenerApi.dispatch(actions.getSystemPerformanceWithEs(layer));
                            listenerApi.signal.addEventListener("abort", function () { return promise.abort(); });
                            return [4 /*yield*/, promise];
                        case 2:
                            _a.sent();
                            return [2 /*return*/];
                    }
                });
            }); },
        });
    }; };
    /*
     * Retry the reveal while the user is waiting for a result.
     * `showPerformanceData` only reveals once EVERY layer has settled, so the
     * moment worth retrying it is when the last layer stops being pending — not
     * when the displayed layer's data happens to change. The two are not the
     * same: the layer that settles last is often the one that isn't displayed.
     */
    var revealInputs = function (state) { return [
        LAYERS.some(function (layer) {
            return state.systemPerformance.byLayer[layer].isPerformanceDataPending ||
                state.systemPerformance.byLayer[layer].isPerformanceDataWithEsPending;
        }),
        selectors.selectPreloadedPerformance(state),
        selectors.selectPerformanceError(state),
    ]; };
    var revealWhenSettled = function () {
        return startAppListening({
            predicate: function (_action, currentState, previousState) {
                if (!selectors.selectIsPerformanceLoading(currentState))
                    return false;
                if (!selectors.selectIsPerformanceLoading(previousState))
                    return true;
                var current = revealInputs(currentState);
                return revealInputs(previousState).some(function (input, i) { return input !== current[i]; });
            },
            effect: function (_action, listenerApi) {
                listenerApi.dispatch(actions.showPerformanceData());
            },
        });
    };
    /*
     * The first calculation of the session presents the charts: the graph panel
     * opens by itself, fades out again after a few seconds (see `GraphPanel`)
     * and the pulsing dashboard buttons show where to find it. It happens once,
     * and not at all once the user has opened or closed the charts themselves.
     *
     * Only once a calculation has produced data the graphs can actually show.
     * When part secured was never reached the data is suppressed (see
     * response-deserializer), so opening the panel would just present an
     * empty/broken result.
     */
    var graphReadiness = function (state) {
        return selectors.selectIsPerformanceDataValid(state) &&
            selectors.selectHasReachedPartSecured(state);
    };
    var openGraphPanelOnResult = function () {
        return startAppListening({
            predicate: function (_action, currentState, previousState) {
                return graphReadiness(currentState) && !graphReadiness(previousState);
            },
            effect: function (_action, listenerApi) {
                var userHasToggledChart = Boolean(sessionStorage.getItem(hasToggledChartStorageKey));
                var hasAutoOpenedChart = Boolean(sessionStorage.getItem(hasAutoOpenedChartStorageKey));
                if (userHasToggledChart || hasAutoOpenedChart)
                    return;
                listenerApi.dispatch(actions.autoOpenGraphPanel());
                listenerApi.dispatch(actions.openMenuItem(MenuItem.APPLICATION_SPEED));
            },
        });
    };
    return __spreadArray(__spreadArray([
        triggerCalculationOnSystemChange
    ], __read(LAYERS.map(triggerEsCalculation)), false), [
        revealWhenSettled,
        openGraphPanelOnResult,
    ], false);
};
